/* global React */
import { LinearProgress } from '@mui/material'
import { getThemeColor } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'

const styles = (theme, { autoHide, duration, color, shouldHide }) => ({
  root: {
    height: '0.25rem', // 4px
    borderRadius: '0.125rem', // 2px
    transition: `opacity ${duration / 2}ms linear`,
    backgroundColor: theme.palette.background.content,
    ...autoHide && { opacity: shouldHide ? 0 : 1 },
    '& .MuiLinearProgress-bar': {
      borderRadius: '0.125rem', // 2px
      backgroundColor: color ? getThemeColor(theme, color) : theme.palette.black.main,
    },
  },
})

/**
 * Progress Bar Element
 *
 * @param {Object} p
 * @param {React.Ref} p.forwardedRef    ref for the element
 * @param {number} [p.duration=300]     ms for the fade transition
 * @param {string} [p.color]            color of the bar, defaults to black
 * @param {boolean} [p.hidden]          whether the component should be hidden
 * @param {boolean} [p.autoHide=true]   whether to only show the bar while loading
 * @param {boolean} [p.indeterminate]   show a sliding animation instead of a value
 * @param {number} p.value              the current loading value
 * @param {Object} [p.containerStyle]   style object applied to the bar (e.g. positioning)
 * @param {Object} [p.backgroundStyle]  style object for the track's background
 * @returns {React.JSX.Element|null}
 */
const ProgressBar = ({
  forwardedRef,
  duration = 300,
  color,
  hidden,
  autoHide = true,
  indeterminate = false,
  value,
  containerStyle,
}) => {
  const maxValue = Math.max(0, Math.min(100, value || 0))
  const shouldHide = !indeterminate && autoHide && (maxValue === 0 || maxValue === 100)
  const classes = useStyles(styles, { shouldHide, autoHide, color, duration })()

  return hidden ? null : (
    <LinearProgress
      ref={forwardedRef}
      value={maxValue}
      variant={indeterminate ? 'indeterminate' : 'determinate'}
      className={`${classes.root} ${containerStyle}`}
    />
  )
}

export default useMemoRef(ProgressBar, props => [props.value, props.indeterminate])
